import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useContent } from "@/context/ContentContext";
import useSWR from "swr";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Gallery() {
  const { content } = useContent();
  const galleryHidden = content?.settings?.gallery_enabled === false;
  const { data: images } = useSWR("/gallery", fetcher);
  const [index, setIndex] = useState(null);
  const list = images || [];
  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + list.length) % list.length), [list.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % list.length), [list.length]);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => { if (e.key === "Escape") close(); if (e.key === "ArrowLeft") prev(); if (e.key === "ArrowRight") next(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, close, prev, next]);

  const touch = React.useRef(null);
  const onTouchStart = (e) => (touch.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touch.current === null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (dx > 50) prev(); else if (dx < -50) next();
    touch.current = null;
  };

  if (galleryHidden) return <Navigate to="/" replace />;

  return (
    <div className="pt-32 pb-24">
      <Seo title="Gallery | Keys Event Center" description="Take a look inside Keys Event Center, our space-themed play structure, arcade, party areas and more." />
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading eyebrow="Take A Look Inside" title="Gallery" subtitle="A peek at the fun waiting for your crew. Tap any photo to explore." />
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {list.map((img, i) => (
            <motion.button key={img.id || i} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05 }}
              onClick={() => setIndex(i)} aria-label={`Open photo ${i + 1}${img.caption ? `: ${img.caption}` : ""}`} className="mb-4 block w-full overflow-hidden rounded-2xl border border-[#2a2a44] group relative" data-testid={`gallery-thumb-${i}`}>
              <img src={img.thumb_url || img.url} alt={img.caption || "Keys Event Center"} loading="lazy" decoding="async" className="w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              {img.caption && <span className="absolute inset-x-0 bottom-0 p-3 text-left text-sm font-semibold text-white bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">{img.caption}</span>}
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {index !== null && list[index] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={close} role="dialog" aria-modal="true" aria-label="Photo viewer" data-testid="lightbox" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <button className="absolute top-5 right-5 text-white/80 hover:text-white p-2" onClick={close} aria-label="Close photo viewer" autoFocus data-testid="lightbox-close"><X size={30} /></button>
            <button className="absolute left-3 sm:left-8 text-white/70 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous photo" data-testid="lightbox-prev"><ChevronLeft size={38} /></button>
            <motion.img key={index} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} src={list[index].url} alt={list[index].caption || ""}
              className="max-h-[85vh] max-w-[90vw] rounded-2xl object-contain" onClick={(e) => e.stopPropagation()} />
            <button className="absolute right-3 sm:right-8 text-white/70 hover:text-white p-2" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next photo" data-testid="lightbox-next"><ChevronRight size={38} /></button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
